import React, { useMemo } from 'react';
import { useFarmerFormValidation } from '@applicationKYC/LegalEntityQuestionnaire/validation/useFarmerFormValidation';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import useWindowDimensions from '@applicationKYC/LegalEntityQuestionnaire/Utils/window-size.service';
import { Field, Loader, Toast } from '@single-platform/components';
import {
  FarmersQuestionnaireHeader,
  PageSubtitle
} from '@applicationKYC/FarmersQuestionnaire/farmersQuestionnaire.style';
import {
  FormStyles,
  PageHeader
} from '@applicationKYC/LegalEntityQuestionnaire/screens/renderPage/renderPage.style';
import { ButtonSection } from '@applicationKYC/LegalEntityQuestionnaire/components';
import { useSelector } from 'react-redux';
import { ButtonSectionWrapperStyle } from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/formStyles';
import { RadioChipOption } from '@applicationKYC/FarmersQuestionnaire/components';
import { FormList } from '@applicationKYC/FarmersQuestionnaire/components/formList/formList';
import breakpointSizes from '@applicationKYC/LegalEntityQuestionnaire/styled-theme-styles/global-style-variables.style';
import { selectKycAppState } from '@redux';

const defaultValues = {
  farmClientType: null,
  farmClients: []
};

const chipOptions = [
  { label: 'farmers_farmer_client_type_companies', value: 'Company' },
  { label: 'farmers_farmer_client_type_individuals', value: 'PrivateIndividual' }
];

export const FarmClientPage = ({ onSubmit, isAtBottom }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { loading, farmerInformation } = useSelector((state) => state.FarmersQuestionnaireReducer);
  const applicationState = useSelector(selectKycAppState);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        farmClientType: Yup.string().required(t('validation_required_field')).nullable(),
        farmClients: Yup.array().when('farmClientType', {
          is: 'Company',
          then: Yup.array()
            .min(1, t('farmers_clients_less_than_one_error'))
            .required(t('validation_required_field'))
        })
      }),
    [t]
  );

  const existingValues = farmerInformation?.farmClientsAndSuppliersSection?.farmClientPage;
  const initialValues = { ...defaultValues, ...existingValues };

  const {
    validationHelpers,
    errors,
    getValues,
    setTouchedOnAll,
    handleSubmit
  } = useFarmerFormValidation({
    initialValues,
    validationSchema
  });

  const onHandleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(
      async (data) => {
        await onSubmit(data, 'farmClientPage');
      },
      () => setTouchedOnAll()
    )();
  };

  if (loading) return <Loader />;

  const values = getValues();

  return (
    <FormStyles small={width < breakpointSizes.phone}>
      <FarmersQuestionnaireHeader>
        <PageHeader>{t('farmers_farm_clients_header')}</PageHeader>
        <PageSubtitle>{t('farmers_farm_clients_subheader')}</PageSubtitle>
      </FarmersQuestionnaireHeader>
      {errors.farmClients && (
        <Toast
          isVisible={!!errors.farmClients}
          animate={true}
          variant="error"
          header={t(errors.farmClients.message)}
        />
      )}
      <Field
        Component={RadioChipOption}
        options={chipOptions}
        value={values.farmClientType}
        name="farmClientType"
        validationHelpers={validationHelpers}
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      {values.farmClientType === 'Company' && (
        <>
          <Toast isVisible animate variant="info" header={t('farmers_farm_clients_info_message')} />
          <FormList
            name="farmClients"
            list={values.farmClients}
            drawerType="companies"
            setList={(updatedList) => validationHelpers.setValue('farmClients', updatedList)}
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />
        </>
      )}
      <ButtonSectionWrapperStyle isAtBottom={isAtBottom}>
        <ButtonSection
          secondButtonClick={onHandleSubmit}
          secondButtonPrimary
          secondButtonText={
            applicationState?.state === 'UserFillingApplication'
              ? t('button_save_and_continue')
              : t('button_continue')
          }
        />
      </ButtonSectionWrapperStyle>
    </FormStyles>
  );
};
